import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Clock, Phone, Instagram, Facebook, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT, waLink, IMAGES } from "../lib/site";
import { Reveal } from "../components/Reveal";
import { WhatsAppIcon, TikTokIcon } from "../components/FloatingWhatsApp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.sent"));
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: MapPin, label: t("contact.where"), value: CONTACT.address, href: CONTACT.mapLink },
    { icon: Clock, label: t("contact.hours"), value: t("contact.hoursValue") },
    ...CONTACT.phones.map((p, i) => ({ icon: Phone, label: t("contact.phone"), value: p.display, href: `tel:${p.tel}`, testid: `contact-phone-${i}` })),
    { icon: WhatsAppIcon, label: "WhatsApp", value: CONTACT.whatsappDisplay, href: waLink("Ciao Mamma Rosa! Vorrei prenotare un tavolo."), testid: "contact-whatsapp" },
  ];

  return (
    <main data-testid="contact-page">
      <section className="relative pt-40 md:pt-52 pb-12 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={IMAGES.coastal} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/60 via-navy/80 to-navy" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-gold/60" />
            <span className="font-body text-xs tracking-[0.3em] uppercase text-gold">{t("contact.kicker")}</span>
          </motion.div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight">{t("contact.title")}</h1>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 grid lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <div className="space-y-px bg-white/10 border border-white/10">
            {info.map((it, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="bg-navy p-7 flex items-start gap-5">
                  <span className="w-11 h-11 grid place-items-center rounded-full border border-gold/40 text-gold shrink-0">
                    <it.icon size={18} />
                  </span>
                  <div>
                    <div className="font-body text-xs tracking-[0.2em] uppercase text-cream/40">{it.label}</div>
                    {it.href ? (
                      <a href={it.href} target="_blank" rel="noopener noreferrer" data-testid={it.testid} className="mt-1 block font-body text-lg text-cream hover:text-gold transition-colors">{it.value}</a>
                    ) : (
                      <div className="mt-1 font-body text-lg text-cream">{it.value}</div>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-6 flex items-center gap-4">
              <span className="font-body text-xs tracking-[0.2em] uppercase text-cream/40">{t("contact.followUs")}</span>
              <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="contact-instagram" className="w-11 h-11 grid place-items-center rounded-full border border-white/15 text-cream/80 hover:border-gold hover:text-gold transition-colors"><Instagram size={18} /></a>
              <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="contact-facebook" className="w-11 h-11 grid place-items-center rounded-full border border-white/15 text-cream/80 hover:border-gold hover:text-gold transition-colors"><Facebook size={18} /></a>
              <a href={CONTACT.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="contact-tiktok" className="w-11 h-11 grid place-items-center rounded-full border border-white/15 text-cream/80 hover:border-gold hover:text-gold transition-colors"><TikTokIcon size={18} /></a>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-6 relative overflow-hidden border border-white/10 h-[280px]">
              <iframe title="map" src={CONTACT.mapEmbed} className="w-full h-full" style={{ border: 0, filter: "grayscale(1) invert(0.9) contrast(0.9)" }} loading="lazy" />
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <a
              href={waLink("Ciao Mamma Rosa! Vorrei prenotare un tavolo.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-book-btn"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-navy-deep font-body font-semibold tracking-wide transition-transform hover:scale-[1.02]"
            >
              {t("common.bookWhatsapp")} <ArrowUpRight size={18} />
            </a>
          </Reveal>
        </div>

        {/* Message form */}
        <Reveal variant="right">
          <div className="border border-white/10 bg-navy-deep p-8 md:p-10">
            <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-8">{t("contact.formTitle")}</h2>
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="font-body text-xs tracking-[0.2em] uppercase text-cream/50">{t("contact.name")}</label>
                <input
                  required
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full bg-transparent border-b border-white/20 py-3 font-body text-cream focus:border-gold outline-none transition-colors"
                />
              </div>
              <div>
                <label className="font-body text-xs tracking-[0.2em] uppercase text-cream/50">Email</label>
                <input
                  required
                  type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 w-full bg-transparent border-b border-white/20 py-3 font-body text-cream focus:border-gold outline-none transition-colors"
                />
              </div>
              <div>
                <label className="font-body text-xs tracking-[0.2em] uppercase text-cream/50">{t("contact.message")}</label>
                <textarea
                  required
                  rows={4}
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full bg-transparent border-b border-white/20 py-3 font-body text-cream focus:border-gold outline-none transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="w-full rounded-full bg-cream px-8 py-4 text-navy-deep font-body font-semibold tracking-wide transition-transform hover:scale-[1.02] disabled:opacity-60"
              >
                {loading ? "..." : t("contact.send")}
              </button>
            </form>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
